import React, { useEffect, useState } from "react";
import { Mail, Save, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { MAILBOX_KEYS, MAILBOX_DEFAULTS } from "../../lib/mailboxes";

/**
 * /admin → Mailboxes panel.
 *
 * Surface to edit the 4 public mailbox addresses that drive every mailto:
 * link and email footer on the public site (Footer, Home "Suggest a Need",
 * Privacy contact, Manage-Subscription help, /contact page).
 *
 * Backed by the CMS content store (keys: mailbox.accounts, mailbox.sales,
 * mailbox.volunteers, mailbox.privacy). Saved values override the defaults
 * on next page load — no redeploy needed.
 */

const LABELS = {
    accounts: { label: "Accounts (financial / receipts)", description: "Donor receipts, subscription help, tax statements, annual reports." },
    sales: { label: "Sales (general inquiries)", description: "Shop orders, partnership requests, community-needs intake, footer email icon." },
    volunteers: { label: "Volunteers", description: "Volunteer onboarding + welcome emails." },
    privacy: { label: "Privacy / data requests", description: "GDPR + data-access + data-deletion + privacy policy contact." },
};

const MailboxesPanel = () => {
    const [values, setValues] = useState({});
    const [original, setOriginal] = useState({});
    const [saving, setSaving] = useState({});

    const load = async () => {
        try {
            // The CMS exposes ALL content as a flat dict via /api/content;
            // we just pick out the mailbox.* keys (with defaults as fallback).
            const { data } = await api.get("/content");
            const next = {};
            MAILBOX_KEYS.forEach((k) => {
                next[k] = data?.[`mailbox.${k}`] || MAILBOX_DEFAULTS[k];
            });
            setValues(next);
            setOriginal(next);
        } catch (e) {
            toast.error("Could not load mailbox settings");
        }
    };
    useEffect(() => { load(); }, []);

    const isValid = (v) => typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
    const isDirty = (k) => values[k] !== original[k];

    const save = async (k) => {
        const v = (values[k] || "").trim();
        if (!isValid(v)) {
            toast.error("Please enter a valid email address");
            return;
        }
        setSaving((s) => ({ ...s, [k]: true }));
        try {
            await api.patch("/admin/content", { key: `mailbox.${k}`, value: v });
            setOriginal((o) => ({ ...o, [k]: v }));
            toast.success(`${LABELS[k].label.split(" (")[0]} mailbox updated → ${v}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving((s) => ({ ...s, [k]: false }));
        }
    };

    const reset = async (k) => {
        // Hit DELETE to clear the override, then reload defaults.
        try {
            await api.delete(`/admin/content/mailbox.${k}`);
        } catch { /* ignore — proceed with local reset */ }
        setValues((v) => ({ ...v, [k]: MAILBOX_DEFAULTS[k] }));
        setOriginal((o) => ({ ...o, [k]: MAILBOX_DEFAULTS[k] }));
        toast.success("Reset to default");
    };

    return (
        <div className="space-y-4" data-testid="admin-mailboxes-panel">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900">
                <p className="font-medium mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" strokeWidth={2} />
                    Public mailbox routing
                </p>
                <p className="text-blue-800">
                    Edit the addresses below to update every <code>mailto:</code> link, footer email, and contact-page address sitewide. Changes take effect on next page load — no deploy needed. Backend-only addresses (admin@, board@) are configured in the server&apos;s <code>.env</code> file and not editable here.
                </p>
            </div>

            {MAILBOX_KEYS.map((k) => (
                <div key={k} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`admin-mailbox-row-${k}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#1C252E]">{LABELS[k].label}</p>
                            <p className="text-xs text-[#4A5568] mt-0.5">{LABELS[k].description}</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
                            <input
                                type="email"
                                value={values[k] || ""}
                                onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                                placeholder={MAILBOX_DEFAULTS[k]}
                                data-testid={`admin-mailbox-input-${k}`}
                                className="font-mono text-sm rounded-xl border border-black/10 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] min-w-0 sm:w-72"
                            />
                            <div className="flex gap-2 flex-shrink-0">
                                {values[k] !== MAILBOX_DEFAULTS[k] && (
                                    <button
                                        onClick={() => reset(k)}
                                        title={`Reset to ${MAILBOX_DEFAULTS[k]}`}
                                        data-testid={`admin-mailbox-reset-${k}`}
                                        className="p-2 rounded-full border border-black/10 hover:border-[#E53935] hover:text-[#E53935] transition-colors"
                                    >
                                        <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                )}
                                <button
                                    onClick={() => save(k)}
                                    disabled={!isDirty(k) || saving[k] || !isValid(values[k] || "")}
                                    data-testid={`admin-mailbox-save-${k}`}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                >
                                    {saving[k] ? (
                                        <>Saving…</>
                                    ) : !isDirty(k) ? (
                                        <><CheckCircle2 className="w-4 h-4" strokeWidth={2} />Saved</>
                                    ) : (
                                        <><Save className="w-4 h-4" strokeWidth={2} />Save</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default MailboxesPanel;
